import { logger } from "@sentry/react";
import { logger as frontendLogger } from "../utils/consoleLogger.ts";
import { getAuthToken } from "./authApi.ts";
import { signal } from "@preact/signals-react";

const errorInSavingConfig = signal("");

if (!process.env.REACT_APP_BASE_URL) {
  frontendLogger.warn(
    "REACT_APP_BASE_URL is not set. Please check your .env file."
  );
}

export const baseUrl = process.env.REACT_APP_BASE_URL || "";

export const getURL = (
  url: string,
  slug: string | boolean,
  query: Record<string, string> | {} = {}
) => {
  const baseSegment = baseUrl?.endsWith("/") ? baseUrl : baseUrl + "/";
  const slugSegment = slug ? `company/${slug}/` : "";
  let urlSegment = url?.startsWith("/") ? url.slice(1) : url;
  urlSegment = urlSegment?.endsWith("/") ? urlSegment : `${urlSegment}/`;
  let querySegment = "";
  if (Object.entries(query)?.length > 0)
    querySegment = `?${Object.entries(query)
      .map(([k, v]) => `${k}=${v}`)
      .join("&")}`;
  const finalApi = baseSegment + slugSegment + urlSegment + querySegment;
  if (finalApi.includes("//")) finalApi.replaceAll("//", "/");
  return finalApi;
};

function delayPromise(seconds: number) {
  return new Promise((resolve, reject) => {
    setTimeout(() => resolve(true), seconds);
  });
}

const JSONParsingError = {
  code: 499,
  error: "Error converting API Response to JSON format",
  status: false,
};

export const handleBookCall = async () => {
  try {
    const authData = JSON.parse(
      localStorage.getItem("babblebots_auth_data") || "{}"
    );
    const token = authData?.token;

    const response = await fetch(
      `${baseUrl}/company/get_acc_manager_meet_link/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (response.ok) {
      const data = await response.json();
      window.open(data.calendly_link, "_blank");
    } else {
      alert("Something went wrong. Please try again later.");
    }
  } catch (error) {
    frontendLogger.error("Error booking call", error);
    alert("Failed to book a call. Please try again later.");
  }
};

export const fetchWithRetry = async (
  url: string,
  opts: RequestInit,
  tries: number = 2,
  delay = 0
) => {
  const errs = [];

  for (let i = 0; i < tries; i++) {
    try {
      frontendLogger.debug(
        `[API] fetchWithRetry attempt ${i + 1}/${tries} ${opts.method || "GET"} ${url}`
      );
      const response = await fetch(url, opts);
      await delayPromise(delay);
      if (response.ok) {
        frontendLogger.info(`[API] fetchWithRetry ${response.status} ${url}`);
        return response;
      } else {
        frontendLogger.warn(
          `[API] fetchWithRetry ${response.status} ${url} (attempt ${i + 1})`
        );
      }
    } catch (err) {
      frontendLogger.error(
        `[API] fetchWithRetry FAILED ${url} (attempt ${i + 1})`,
        err
      );
    }
  }
};

export const postWithRetry = async (
  url: string,
  body: any,
  tries: number = 2,
  delay = 0
) => {
  if (!url.startsWith("http")) {
    url = baseUrl + url;
  }
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
    },
    body: JSON.stringify(body),
  };
  return fetchWithRetry(url, requestOptions, tries, delay);
};

export const get = async (
  url: string,
  useBaseUrl: boolean = true,
  token = null
) => {
  if (useBaseUrl && !url.startsWith("http")) {
    url = baseUrl + url;
  }
  frontendLogger.debug(`[API] GET ${url}`);
  try {
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: token
          ? "Bearer " + token
          : getAuthToken()
            ? "Bearer " + getAuthToken()
            : "",
        Accept: "application/json",
      },
    });
    if (response.ok) {
      frontendLogger.info(`[API] GET ${response.status} ${url}`);
      try {
        return await response.json().catch((res) => {
          throw new Error("GET: error converting response json");
        });
      } catch (e) {
        return e;
      }
    } else {
      frontendLogger.error(`[API] GET ${response.status} ${url}`);
      throw new Error(`${response.status}`);
    }
  } catch (e) {
    frontendLogger.error(`[API] GET FAILED ${url}`, e);
    throw e;
  }
};

export const getWithURL = async (api_route: string) => {
  frontendLogger.debug(`[API] GET ${api_route}`);
  const response = await fetch(api_route, {
    headers: {
      "Content-Type": "application/json",
      // Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
      Accept: "application/json",
    },
  });
  // Parsing response into JSON
  let json;
  try {
    json = await response.json();
  } catch (error) {
    frontendLogger.error(`[API] GET ${api_route} — JSON parse failed`, error);
    logger.error("JSON Parsing error", {
      error: "JSONError",
      url: api_route,
      payload: response,
      user: getAuthToken()?.email ?? "",
    });
  }
  if (response?.ok) {
    frontendLogger.info(`[API] GET ${response.status} ${api_route}`, {
      data: json,
    });
    return {
      code: response.status,
      data: json,
      status: response.ok,
      error: false,
    };
  } else {
    frontendLogger.error(`[API] GET ${response.status} ${api_route}`, {
      error: json?.message || response?.statusText,
      body: json,
    });
    logger.error("GET request failed", {
      error: json?.message || response?.statusText,
      status: response?.status,
      payload: json,
      url: api_route,
      user: getAuthToken()?.email ?? "",
    });
    throw new Error("GET request failed", json);
  }
};

export const getAPI = async (
  url: string,
  slug: false | string = false,
  queryParams: Record<string, string> = {}
) => {
  let api_route = getURL(url, slug, queryParams);
  frontendLogger.debug(`[API] GET ${api_route}`);
  // Calling Fetch
  const response = await fetch(api_route, {
    headers: {
      "Content-Type": "application/json",
      Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
      Accept: "application/json",
    },
  });
  // Parsing response into JSON
  let json;
  try {
    json = await response.json();
  } catch (error) {
    if (!response?.ok)
      frontendLogger.log(`[API] GET ${api_route} — JSON parse failed`, error);
  }
  if (response?.ok) {
    frontendLogger.info(`[API] GET ${response.status} ${api_route}`, {
      data: json,
    });
    return {
      code: response.status,
      data: json ?? "",
      status: response.ok,
      error: false,
    };
  } else {
    frontendLogger.error(`[API] GET ${response.status} ${api_route}`, {
      error: json?.message || response?.statusText,
      body: json,
    });
    const requestError = new Error("GET request failed");
    (requestError as any).status = response.status;
    throw requestError;
  }
};

export const postAPI = async (
  url: string,
  body: any,
  slug: string | boolean = false,
  query = {},
  returnOnError = false
) => {
  const api_route = getURL(url, slug, query);
  frontendLogger.debug(`[API] POST ${api_route}`, { body });
  const response = await fetch(api_route, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  let json;
  try {
    json = await response.json();
  } catch (error) {
    if (!response?.ok)
      frontendLogger.debug(
        `[API] POST ${api_route} — JSON parse failed`,
        error
      );
  }
  if (response.ok) {
    frontendLogger.info(`[API] POST ${response.status} ${api_route}`, {
      data: json,
    });
    return {
      code: response.status,
      data: json ?? "",
      status: response.ok,
      error: false,
    };
  } else {
    frontendLogger.error(`[API] POST ${response.status} ${api_route}`, {
      error: json?.message || response?.statusText,
      body: json,
    });
    if (response.status === 409) {
      frontendLogger.error("Cannot add duplicate", {
        error: json?.message || response?.statusText,
        status: response?.status,
        payload: json,
        url: api_route,
        user: getAuthToken()?.email ?? "",
      });
      throw new Error("POST<JSONBody> request failed", json);
    }
    frontendLogger.error("POST<JSONBody> request failed", {
      error: json?.message || response?.statusText,
      status: response?.status,
      payload: json,
      url: api_route,
      user: getAuthToken()?.email ?? "",
    });
    if (returnOnError) {
      return {
        code: response.status,
        data: json ?? "",
        status: false,
        error: json?.error || json?.message || response?.statusText,
      };
    }
    throw new Error("POST<JSONBody> request failed", json);
  }
};

export const putAPI = async (
  url: string,
  body: any,
  slug: string | boolean = false,
  query = {},
  returnOnError = false
) => {
  const api_route = getURL(url, slug, query);
  frontendLogger.debug(`[API] PUT ${api_route}`, { body });
  const response = await fetch(api_route, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  let json;
  try {
    json = await response.json();
  } catch (error) {
    if (!response?.ok)
      frontendLogger.error(`[API] PUT ${api_route} — JSON parse failed`, error);
  }
  if (response.ok) {
    frontendLogger.info(`[API] PUT ${response.status} ${api_route}`, {
      data: json,
    });
    return {
      code: response.status,
      data: json ?? "",
      status: response.ok,
      error: false,
    };
  } else {
    frontendLogger.error(`[API] PUT ${response.status} ${api_route}`, {
      error: json?.message || response?.statusText,
      body: json,
    });
    if (returnOnError) {
      return {
        code: response.status,
        data: json ?? "",
        status: false,
        error:
          json?.error ||
          json?.message ||
          json?.detail ||
          (Array.isArray(json?.non_field_errors)
            ? json.non_field_errors[0]
            : "") ||
          response?.statusText,
      };
    }
    throw new Error("PUT<JSONBody> request failed", json);
  }
};

export const postWithFormdata = async (
  url: string,
  form: FormData,
  slug = false,
  query = []
) => {
  let api_route = getURL(url, slug, query);
  frontendLogger.debug(`[API] POST(FormData) ${api_route}`);
  const response = await fetch(api_route, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
      Accept: "application/json",
    },
    body: form,
  });
  let json;
  try {
    json = await response.json();
  } catch (error) {
    frontendLogger.error(
      `[API] POST(FormData) ${api_route} — JSON parse failed`,
      error
    );
    logger.error("JSON Parsing error", {
      error: "JSONError",
      url: api_route,
      payload: response,
      user: getAuthToken()?.email ?? "",
    });
  }
  if (response.ok) {
    frontendLogger.info(
      `[API] POST(FormData) ${response.status} ${api_route}`,
      { data: json }
    );
    return {
      code: response.status,
      data: json,
      status: response.ok,
      error: false,
    };
  } else {
    frontendLogger.error(
      `[API] POST(FormData) ${response.status} ${api_route}`,
      { error: json?.message || response?.statusText, body: json }
    );
    if (response.status === 409) {
      logger.error("Cannot add duplicate", {
        error: json?.message || response?.statusText,
        status: response?.status,
        payload: json,
        url: api_route,
        user: getAuthToken()?.email ?? "",
      });
      throw new Error("POST<JSONBody> request failed", json);
    }
    logger.error("POST<JSONBody> request failed", {
      error: json?.message || response?.statusText,
      status: response?.status,
      payload: json,
      url: api_route,
      user: getAuthToken()?.email ?? "",
    });
    throw new Error("POST<JSONBody> request failed", json);
  }
};

export const httpDelete = async (url: string) => {
  if (!url.startsWith("http")) {
    url = baseUrl + url;
  }
  frontendLogger.debug(`[API] DELETE ${url}`);
  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
        Accept: "application/json",
      },
    });
    if (response.ok) {
      frontendLogger.info(`[API] DELETE ${response.status} ${url}`);
      try {
        return await response.json().catch((res) => {
          throw new Error("DELETE: error converting response json");
        });
      } catch (e) {
        return e;
      }
    } else {
      frontendLogger.error(`[API] DELETE ${response.status} ${url}`);
      throw await response.json();
    }
  } catch (e) {
    frontendLogger.error(`[API] DELETE FAILED ${url}`, e);
    throw e;
  }
};

export const deleteWithBody = async (url: string, body: any) => {
  if (!url.startsWith("http")) {
    url = baseUrl + url;
  }
  frontendLogger.debug(`[API] DELETE ${url}`, { body });
  try {
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
    if (response.ok) {
      frontendLogger.info(`[API] DELETE ${response.status} ${url}`);
      try {
        return await response.json().catch((res) => {
          throw new Error("DELETE: error converting response json");
        });
      } catch (e) {
        return e;
      }
    } else {
      frontendLogger.error(`[API] DELETE ${response.status} ${url}`);
      throw await response.json();
    }
  } catch (e) {
    frontendLogger.error(`[API] DELETE FAILED ${url}`, e);
    throw e;
  }
};

export const post = async (
  url: string,
  body: any,
  useBaseUrl: boolean = true
) => {
  /*if (useBaseUrl && !url.startsWith("http")) {
    url = baseUrl + url;
    Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
  }*/
  url = baseUrl + url;
  frontendLogger.debug(`[API] POST ${url}`, { body });
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
    if (response.ok) {
      frontendLogger.info(`[API] POST ${response.status} ${url}`);
      try {
        return await response.json().catch((res) => {
          throw new Error("POST: error converting response json");
        });
      } catch (e) {
        return e;
      }
    } else {
      frontendLogger.error(`[API] POST ${response.status} ${url}`, {
        status: response.status,
      });
      if (response.status === 409)
        errorInSavingConfig.value = "Cannot add duplicate";
      if (response.status === 400) return "Failed";
      // throw new Error(`${response.status}`);
      const errorData = await response.json().catch(() => ({}));
      throw { status: response.status, error: errorData };
    }
  } catch (e) {
    frontendLogger.error(`[API] POST FAILED ${url}`, e);
    throw e;
  }
};

export const patchAPI = async (
  url: string,
  body: any,
  slug: string | boolean = false,
  query = {}
) => {
  const api_route = getURL(url, slug, query);
  frontendLogger.debug(`[API] PATCH ${api_route}`, { body });
  const response = await fetch(api_route, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  let json;
  try {
    json = await response.json();
  } catch (error) {
    if (!response?.ok)
      frontendLogger.error(`[API] PATCH ${api_route} — JSON parse failed`, error);
  }
  if (response.ok) {
    frontendLogger.info(`[API] PATCH ${response.status} ${api_route}`, { data: json });
    return {
      code: response.status,
      data: json ?? "",
      status: response.ok,
      error: false,
    };
  } else {
    frontendLogger.error(`[API] PATCH ${response.status} ${api_route}`, {
      error: json?.message || response?.statusText,
      body: json,
    });
    throw new Error("PATCH<JSONBody> request failed", json);
  }
};

export const patch = async (url: string, body: any) => {
  if (!url.startsWith("http")) {
    url = baseUrl + url;
  }
  frontendLogger.debug(`[API] PATCH ${url}`, { body });
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  if (response.ok) {
    frontendLogger.info(`[API] PATCH ${response.status} ${url}`);
  } else {
    frontendLogger.error(`[API] PATCH ${response.status} ${url}`);
  }
  try {
    return await response.json().catch((res) => res);
  } catch (e) {
    return e;
  }
};

export function patchFormData<T>(url: string, data: FormData): Promise<T> {
  if (!url.startsWith("http")) {
    url = baseUrl + url;
  }
  return new Promise<T>((resolve, reject) => {
    const httpRequest = new XMLHttpRequest();
    httpRequest.open("PATCH", url);

    const authHeader = getAuthToken() ? "Bearer " + getAuthToken() : "";
    if (authHeader) {
      httpRequest.setRequestHeader("Authorization", authHeader);
    }
    httpRequest.setRequestHeader("Accept", "application/json");

    httpRequest.onreadystatechange = (res: any) => {
      if (httpRequest.readyState == XMLHttpRequest.DONE) {
        frontendLogger.info("FormData patch complete", {
          status: httpRequest.status,
          url,
        });
        if (httpRequest.status < 400) {
          resolve(
            httpRequest.responseText
              ? JSON.parse(httpRequest.responseText)
              : null
          );
        } else {
          reject(res);
        }
      }
    };

    httpRequest.onerror = (res: any) => {
      frontendLogger.error("Error patching FormData", { url });
      reject(res);
    };

    httpRequest.send(data);
  });
}

export const put = async (url: string, body: any) => {
  if (!url.startsWith("http")) {
    url = baseUrl + url;
  }
  frontendLogger.debug(`[API] PUT ${url}`, { body });
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: getAuthToken() ? "Bearer " + getAuthToken() : "",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  if (response.ok) {
    frontendLogger.info(`[API] PUT ${response.status} ${url}`);
  } else {
    frontendLogger.error(`[API] PUT ${response.status} ${url}`);
  }
  try {
    return await response.json().catch((res) => res);
  } catch (e) {
    return e;
  }
};

export function postFormData<T>(url: string, data: FormData): Promise<T> {
  if (!url.startsWith("http")) {
    url = baseUrl + url;
  }
  return new Promise<T>((resolve, reject) => {
    const httpRequest = new XMLHttpRequest();
    httpRequest.open("POST", url);

    const authHeader = getAuthToken() ? "Bearer " + getAuthToken() : "";
    if (authHeader) {
      httpRequest.setRequestHeader("Authorization", authHeader);
    }
    httpRequest.setRequestHeader("Accept", "application/json");

    httpRequest.onreadystatechange = (res: any) => {
      if (httpRequest.readyState == XMLHttpRequest.DONE) {
        frontendLogger.info("FormData upload complete", {
          status: httpRequest.status,
          url,
        });
        if (httpRequest.status < 400) {
          resolve(
            httpRequest.responseText
              ? JSON.parse(httpRequest.responseText)
              : null
          );
        } else {
          reject(res);
        }
      }
    };

    httpRequest.onerror = (res: any) => {
      frontendLogger.error("Error uploading FormData", { url });
      reject(res);
    };

    httpRequest.send(data);
  });
}
export const download = async (
  url: string,
  fileName: string,
  useBaseUrl: boolean = true,
  token = null
) => {
  if (useBaseUrl && !url.startsWith("http")) {
    url = baseUrl + url;
  }
  frontendLogger.debug(`[API] DOWNLOAD ${url}`, { fileName });
  try {
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: token
          ? "Bearer " + token
          : getAuthToken()
            ? "Bearer " + getAuthToken()
            : "",
        Accept: "application/json",
      },
    });
    if (response.ok) {
      frontendLogger.info(`[API] DOWNLOAD ${response.status} ${url}`);
      try {
        const blob = await response.blob().catch((res) => {
          throw new Error("GET: error converting response json");
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || "candidates.csv"; // Specify the desired filename and extension
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        return e;
      }
    } else {
      frontendLogger.error(`[API] DOWNLOAD ${response.status} ${url}`);
      throw new Error(`${response.status}`);
    }
  } catch (e) {
    frontendLogger.error(`[API] DOWNLOAD FAILED ${url}`, e);
    throw e;
  }
};
